import React, { useState, useEffect } from 'react';
import { 
    FileBarChart, Plus, RefreshCw, AlertCircle, CalendarClock, Lock, CheckCircle2,
    Play, Eye, Edit3, Archive, FileText, ChevronRight, Calculator, Loader2,
    CheckCircle, HelpCircle, XCircle, Trash2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { format, differenceInDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import CustomReportWizard from './CustomReportWizard';

export default function CustomReportsPage({ handleNavigation }) {
    const [reports, setReports] = useState([]);
    const [quotaStatus, setQuotaStatus] = useState({ isReportWizardEnabled: false, quota: 0, used: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [editReportId, setEditReportId] = useState(null);

    // Modal States
    const [modalConfig, setModalConfig] = useState({ isOpen: false, type: 'info', title: '', message: '', onCloseCallback: null });
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const showModal = (type, title, message, onCloseCallback = null) => {
        setModalConfig({ isOpen: true, type, title, message, onCloseCallback });
    };
    const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));
    const closeConfirm = () => setConfirmConfig(prev => ({ ...prev, isOpen: false }));

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [quotaRes, reportsRes] = await Promise.all([
                apiClient('reports/custom/quota-status'),
                apiClient('reports/custom')
            ]);
            setQuotaStatus(quotaRes);
            setReports(reportsRes);
        } catch (e) {
            setError(e.message || "Veriler yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleArchive = (id, name) => {
        setConfirmConfig({
            isOpen: true,
            title: "Raporu Sil",
            message: `'${name}' isimli özel raporu silmek istediğinize emin misiniz? Silinen raporlar geri yüklenemez ve kotanızda tekrar yer açılır (eğer 10 günlük kilit süresinde değilse).`,
            onConfirm: async () => {
                closeConfirm();
                try {
                    await apiClient(`reports/custom/${id}`, { method: 'DELETE' });
                    showModal("success", "Başarılı", "Rapor başarıyla silindi.");
                    loadData();
                } catch (e) {
                    let errorMsg = e.message;
                    if (errorMsg && errorMsg.includes('silinemez')) {
                        errorMsg = "Bu raporun 10 günlük kilit süresi dolmuştur. Kotanızı korumak adına kilitlenmiş raporlar silinemez. Lütfen yöneticiniz ile iletişime geçin.";
                    }
                    showModal("error", "Silme İşlemi Başarısız", errorMsg);
                }
            }
        });
    };

    const isQuotaFull = quotaStatus.used >= quotaStatus.quota;

    return (
        <div className="flex-1 w-full bg-gray-50 dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-colors duration-300 overflow-hidden min-h-[calc(100vh-64px)]">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white dark:bg-gray-800">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                        <FileBarChart className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Özel Raporlar (Wizards)</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Firmanıza özel dinamik raporlar oluşturun ve yönetin.</p>
                    </div>
                </div>
                <div className="mt-4 sm:mt-0 flex gap-3">
                    <button
                        onClick={loadData}
                        className="flex items-center px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition shadow-sm"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Yenile
                    </button>
                    <button
                        onClick={() => { setEditReportId(null); setWizardOpen(true); }}
                        disabled={isQuotaFull}
                        className={`flex items-center px-4 py-2 text-white rounded-lg shadow-md transition transform ${isQuotaFull ? 'bg-gray-400 cursor-not-allowed opacity-70' : 'bg-blue-600 hover:bg-blue-700 hover:scale-105'}`}
                        title={isQuotaFull ? "Özel rapor hakkınız dolmuştur." : "Yeni rapor oluştur"}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Rapor Oluştur
                    </button>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                        <p>{error}</p>
                    </div>
                )}

                {/* Quota Card */}
                {!loading && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 flex items-center justify-between shadow-sm">
                        <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rapor Kullanım Hakkı</span>
                            <div className="flex items-baseline gap-2">
                                <span className={`text-3xl font-bold ${isQuotaFull ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                    {quotaStatus.used}
                                </span>
                                <span className="text-xl text-gray-400 dark:text-gray-500">/ {quotaStatus.quota}</span>
                            </div>
                        </div>
                        {isQuotaFull && (
                            <div className="flex items-center text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-full">
                                <AlertCircle className="w-4 h-4 mr-1.5" /> Kota Dolu
                            </div>
                        )}
                        {!isQuotaFull && quotaStatus.quota > 0 && (
                            <div className="flex items-center text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 px-3 py-1.5 rounded-full">
                                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Kullanılabilir Hak: {quotaStatus.quota - quotaStatus.used}
                            </div>
                        )}
                    </div>
                )}

                {/* Reports List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">Rapor Adı</th>
                                    <th className="px-6 py-4 font-semibold">Kaynak</th>
                                    <th className="px-6 py-4 font-semibold">Oluşturan</th>
                                    <th className="px-6 py-4 font-semibold">Tarih</th>
                                    <th className="px-6 py-4 font-semibold">Durum</th>
                                    <th className="px-6 py-4 font-semibold text-right">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
                                            Yükleniyor...
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                            <FileText className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                                            Henüz oluşturulmuş özel rapor bulunmuyor.
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map(r => {
                                        const createdAt = new Date(r.createdAt);
                                        const now = new Date();
                                        const daysPassed = differenceInDays(now, createdAt);
                                        const canEdit = r.isEditable;
                                        
                                        return (
                                            <tr key={r.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${!r.isActive ? 'opacity-60 bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-800'}`}>
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                        {r.name}
                                                        {!r.isActive && <span className="px-2 py-0.5 text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded uppercase font-bold">Arşivli</span>}
                                                    </div>
                                                    {r.description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-[200px] truncate">{r.description}</div>}
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-300 font-medium">
                                                    {r.baseReportType === 'Puantaj Özeti' || r.baseReportType === 'puantaj_raporu' ? 'Puantaj Özeti (Gün Bazlı)' : r.baseReportType === 'puantaj_raporu_summary' ? 'Puantaj Özeti (Personel Bazlı Toplam)' : r.baseReportType}
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                                                    {r.createdByName || 'Bilinmiyor'}
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                                                    <div className="flex flex-col">
                                                        <span>{format(createdAt, 'dd.MM.yyyy')}</span>
                                                        <span className="text-[10px] text-gray-500">{format(createdAt, 'HH:mm')}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {!r.isActive ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                            <Archive className="w-3 h-3 mr-1" /> Arşivlendi
                                                        </span>
                                                    ) : canEdit ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                                                            <Edit3 className="w-3 h-3 mr-1" /> Düzenlenebilir ({10 - daysPassed} gün kaldı)
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                                            <Lock className="w-3 h-3 mr-1" /> Kilitli
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button 
                                                            onClick={() => handleNavigation && handleNavigation(`run_custom_report_${r.id}`)}
                                                            className="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition disabled:opacity-50"
                                                            title="Raporu Çalıştır"
                                                            disabled={!r.isActive}
                                                        >
                                                            <Play className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => { setEditReportId(r.id); setWizardOpen(true); }}
                                                            className={`p-1.5 rounded transition ${canEdit && r.isActive ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}
                                                            title={canEdit ? "Düzenle" : "Süre doldu (Kilitli)"}
                                                            disabled={!canEdit || !r.isActive}
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleArchive(r.id, r.name)}
                                                            className="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/50 transition disabled:opacity-50"
                                                            title="Arşivle"
                                                            disabled={!r.isActive}
                                                        >
                                                            <Archive className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {wizardOpen && (
                <CustomReportWizard 
                    reportId={editReportId} 
                    onClose={() => { setWizardOpen(false); loadData(); }} 
                />
            )}

            {/* Error/Success Modal Overlay */}
            {modalConfig.isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className={`p-4 border-b ${modalConfig.type === 'error' ? 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-900/30' : 'bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-900/30'} flex items-center gap-3`}>
                            {modalConfig.type === 'error' ? <AlertCircle className="w-6 h-6 text-red-500" /> : <CheckCircle className="w-6 h-6 text-green-500" />}
                            <h3 className={`font-semibold ${modalConfig.type === 'error' ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                                {modalConfig.title}
                            </h3>
                        </div>
                        <div className="p-5 text-gray-600 dark:text-gray-300">
                            {modalConfig.message}
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 flex justify-end">
                            <button 
                                onClick={() => {
                                    closeModal();
                                    if (modalConfig.onCloseCallback) modalConfig.onCloseCallback();
                                }}
                                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition"
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmConfig.isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-4 border-b border-orange-100 dark:border-orange-900/30 bg-orange-50 dark:bg-orange-900/20 flex items-center gap-3">
                            <HelpCircle className="w-6 h-6 text-orange-500" />
                            <h3 className="font-semibold text-orange-700 dark:text-orange-400">
                                {confirmConfig.title}
                            </h3>
                        </div>
                        <div className="p-5 text-gray-600 dark:text-gray-300">
                            {confirmConfig.message}
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 flex justify-end gap-3">
                            <button 
                                onClick={closeConfirm}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                            >
                                İptal
                            </button>
                            <button 
                                onClick={confirmConfig.onConfirm}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" /> Evet, Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
